/**
 * Minimaler IndexedDB-Wrapper (kein Framework). Zwei Stores:
 *  - "kv"     : Schlüssel/Wert (Menü-Snapshot, persistierter aktueller Bon)
 *  - "outbox" : noch nicht synchronisierte Events (autoIncrement-Schlüssel `seq`)
 */

const DB_NAME = "orderman";
const DB_VERSION = 2;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv");
      if (!db.objectStoreNames.contains("outbox")) {
        db.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
      }
      // v2: lokale Historie der Bestellungen dieses Geräts ("Meine Bons").
      if (!db.objectStoreNames.contains("orders")) {
        db.createObjectStore("orders", { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const req = fn(tx.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---- kv ----------------------------------------------------------------------

export function kvGet<T>(key: string): Promise<T | undefined> {
  return run<T | undefined>("kv", "readonly", (s) => s.get(key) as IDBRequest<T | undefined>);
}

export function kvSet<T>(key: string, value: T): Promise<IDBValidKey> {
  return run("kv", "readwrite", (s) => s.put(value as unknown as object, key));
}

export function kvDelete(key: string): Promise<undefined> {
  return run("kv", "readwrite", (s) => s.delete(key) as IDBRequest<undefined>);
}

export function kvKeys(): Promise<string[]> {
  return run<IDBValidKey[]>("kv", "readonly", (s) => s.getAllKeys()).then(
    (keys) => keys.map(String),
  );
}

// ---- outbox ------------------------------------------------------------------

export interface OutboxRecord<E = unknown> {
  seq: number;
  env: E;
}

export function outboxAdd<E>(env: E): Promise<IDBValidKey> {
  return run("outbox", "readwrite", (s) => s.add({ env } as object));
}

export function outboxAll<E>(): Promise<OutboxRecord<E>[]> {
  return run<OutboxRecord<E>[]>("outbox", "readonly", (s) => s.getAll() as IDBRequest<OutboxRecord<E>[]>);
}

// ---- orders (lokale Bon-Historie) -------------------------------------------

export function ordersPut<T extends { id: string }>(rec: T): Promise<IDBValidKey> {
  return run("orders", "readwrite", (s) => s.put(rec as object));
}

export function ordersGetAll<T>(): Promise<T[]> {
  return run<T[]>("orders", "readonly", (s) => s.getAll() as IDBRequest<T[]>);
}

export function ordersClear(): Promise<undefined> {
  return run("orders", "readwrite", (s) => s.clear() as IDBRequest<undefined>);
}

export function outboxClear(): Promise<undefined> {
  return run("outbox", "readwrite", (s) => s.clear() as IDBRequest<undefined>);
}

export async function outboxDelete(seqs: number[]): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction("outbox", "readwrite");
    const store = tx.objectStore("outbox");
    for (const seq of seqs) store.delete(seq);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
